import Action from "../base/action";
import LoadTimeline from "../base/load-timeline";
import Prune from "../base/prune";
import { ResourceTypeEnum, TriggerTypeEnum } from "../base/training-literals";

class Wayfinding extends Action {
    until;
    distanceThreshold;
    pathColor;
    timelines;
    prune;
    selectedPoint;
    volume;
    model;
    visible;
    _stopper;

    constructor(props) {
        super(props);
        this.until = props.until;
        this.distanceThreshold = !isNullOrUndefined(props.distanceThreshold) ? props.distanceThreshold : WayfindingConstants.distanceThreshold;
        this.timelines = props.timelines || [];
        this.events = props.events || [];
        this.triggerType = props.triggerType || TriggerTypeEnum.TIMELINE;
        this.prune = new Prune(props.prune || {});
        this.pathColor = props.pathColor || WayfindingConstants.pathColor;
        const timelineContainer = {
            timelines: this.timelines,
            events: this.events,
            triggerType: this.triggerType,
            prune: this.prune
        };
        this.selectedPoint = props.selectedPoint ? new THREE.Vector3(props.selectedPoint.x, props.selectedPoint.y, props.selectedPoint.z) : new THREE.Vector3();
        this.model = props.model || {id: "", name: ""};
        this.volume = props.volume || {id: "", name: ""};
        this.visible = isNullOrUndefined(props.visible) ? true : props.visible;

        if ((!this.timelines || this.timelines.length === 0) && (!this.events || this.events.length === 0)) {
            this.defaultAction = null;
        } else {
            this.defaultAction = timelineContainer && new LoadTimeline(timelineContainer);
        }

        this.id = generateUUID();
        this.wayfindingLine = null;
        this._stopper = () => {
        };
    }

    async execute(eventLoop) {
        this.wayfindingLine = null;
        if (!this.model.id) return;
        const modelResource = eventLoop.training.resources[this.model?.id];
        this.modelName = modelResource?.name;
        this.modelId = modelResource?.resourceId?.toString() || (modelResource?.type === ResourceTypeEnum.HELPER_OBJECT && modelResource?.id);
        let self = this;

        wayfindingManager.addWayfindingAction(this);
        this._stopper = activateWayfinding();
        setTimeout(this._stopper, self.until - self.startTime);

        await self.performWayfinding(eventLoop);

        function activateWayfinding() {
            document.addEventListener('activate_wayfinding', handleWayfinding);
            return () => {
                document.removeEventListener('activate_wayfinding', handleWayfinding);
                wayfindingManager.removeWayfindingLine(self.id);
            }
        }

        async function handleWayfinding() {
            await self.performWayfinding(eventLoop);
        }
    }

    async performWayfinding(eventLoop) {
        if (!wayfindingManager.getWayfindingAction(this.actionId)) {
            return;
        }

        const wayfindingPerformed = this.performWayfindingCalculations();
        if (wayfindingPerformed) {
            return;
        }

        const nextAction = this.defaultAction;
        if (nextAction) {
            this._stopper();
            await nextAction.execute(eventLoop);
        }
    }

    performWayfindingCalculations() {
        const cameraPos = iq3Camera.getConfigurationWorld().position;
        const fromPos = this.getStartPosition();
        const toPos = this.getDestinationPosition();
        const distance = cameraPos.distanceTo(toPos);

        if (distance >= this.distanceThreshold) {
            this.updateWayfindingLine(fromPos, toPos);
            return true;
        }

        wayfindingManager.removeWayfindingLine(this.id);
        this.wayfindingLine = null;
        return false;
    }

    stopWayfinding() {
        if (this._stopper) {
            this._stopper();
        }

        wayfindingManager.removeWayfindingLine(this.id);
        this.wayfindingLine = null;
    }

    updateWayfindingLine(startPosition, endPosition) {
        const lineColorHex = rgbToHexColorCode(this.pathColor.r, this.pathColor.g, this.pathColor.b);

        if (!this.wayfindingLine) {
            this.createWayfindingLine(startPosition, endPosition, lineColorHex);
        } else {
            if (this.wayfindingLine?.startPoint.equals(startPosition) && this.wayfindingLine?.endPoint.equals(endPosition)) {
                return this.wayfindingLine;
            }

            this.modifyWayfindingLine(startPosition, endPosition, lineColorHex);
        }

        this.updateVisibilityOfWayfindingLine(this.visible);
    }

    createWayfindingLine(startPoint, endPoint, lineColorHex) {
        const points = this.computeWayfindingPoints(startPoint, endPoint);
        this.wayfindingLine = new THREE.Group();
        this.wayfindingLine.lineId = this.id;
        this.wayfindingLine.startPoint = startPoint.clone();
        this.wayfindingLine.endPoint = endPoint.clone();
        this.prepareUserData();

        const curveMesh = this.createCurveMesh(points, lineColorHex);
        this.wayfindingLine.add(curveMesh);

        const destinationSphere = this.createDestinationSphere(endPoint);
        this.wayfindingLine.add(destinationSphere);

        wayfindingManager.addWayfindingLine(this.wayfindingLine);
    }

    modifyWayfindingLine(startPoint, endPoint, lineColorHex) {
        const points = this.computeWayfindingPoints(startPoint, endPoint);
        this.wayfindingLine.children[0].geometry.setFromPoints(points);
        this.wayfindingLine.children[0].material.color.set(lineColorHex);
        this.wayfindingLine.children[0].material.opacity = this.pathColor.a || 1;
        this.wayfindingLine.children[0].material.needsUpdate = true;

        this.wayfindingLine.children[1].position.copy(endPoint);

        this.wayfindingLine.startPoint = startPoint.clone();
        this.wayfindingLine.endPoint = endPoint.clone();
    }

    computeWayfindingPoints(startPoint, endPoint) {
        const cameraPos = iq3Camera.getConfigurationWorld().position;
        const distance = cameraPos.distanceTo(endPoint);
        const maxCurvature = WayfindingConstants.maxCurvature;
        let curvatureFactor = Math.min(distance / WayfindingConstants.maxDistanceMultiplier, WayfindingConstants.maxCurvatureFactor);
        curvatureFactor = Math.max(curvatureFactor, WayfindingConstants.minCurvatureFactor);

        const controlPoint1 = new THREE.Vector3(
            startPoint.x + maxCurvature * curvatureFactor,
            startPoint.y + maxCurvature * curvatureFactor,
            startPoint.z
        );

        const controlPoint2 = new THREE.Vector3(
            endPoint.x - maxCurvature * curvatureFactor,
            endPoint.y + maxCurvature * curvatureFactor,
            endPoint.z
        );

        const curve = new THREE.CubicBezierCurve3(startPoint, controlPoint1, controlPoint2, endPoint);
        const totalPoints = this.calculateTotalPoints(distance);
        return curve.getPoints(totalPoints);
    }

    calculateTotalPoints(distance) {
        const minPoints = WayfindingConstants.curvePoints.min;
        const maxPoints = WayfindingConstants.curvePoints.max;
        const maxDistance = WayfindingConstants.maxCurveDistance;

        const points = minPoints + ((distance / maxDistance) * (maxPoints - minPoints));
        return Math.floor(Math.max(minPoints, Math.min(maxPoints, points)));
    }

    createCurveMesh(points, lineColorHex) {
        const circleTexture = this.getCircleTexture();
        const pointsMaterial = new THREE.PointsMaterial({
            color: lineColorHex,
            opacity: this.pathColor ? this.pathColor.a : 1,
            side: THREE.DoubleSide,
            size: WayfindingConstants.curveMesh.pointSize,
            map: circleTexture,
            alphaTest: WayfindingConstants.curveMesh.alphaTest,
            transparent: true,
            depthTest: false,
            depthWrite: true,
            forceSinglePass: true,
        });

        const pointsGeometry = new THREE.BufferGeometry().setFromPoints(points);
        const curveMesh = new THREE.Points(pointsGeometry, pointsMaterial);
        curveMesh.renderOrder = WayfindingConstants.curveMesh.renderOrder;
        curveMesh.frustumCulled = false;
        return curveMesh;
    }

    createDestinationSphere(position) {
        const sphereConstants = WayfindingConstants.destinationSphere;
        const destinationSphereGeometry = new THREE.SphereGeometry(sphereConstants.radius, sphereConstants.widthSegments, sphereConstants.heightSegments);
        const destinationSphereMaterial = new THREE.MeshBasicMaterial({
            color: sphereConstants.color,
            side: THREE.DoubleSide,
            sizeAttenuation: false,
            forceSinglePass: true,
            depthTest: false,
            depthWrite: true,
            transparent: true,
        });

        let destinationSphere = new THREE.Mesh(destinationSphereGeometry, destinationSphereMaterial);
        destinationSphere.position.copy(position);
        destinationSphere.name = sphereConstants.name;
        destinationSphere.renderOrder = sphereConstants.renderOrder;
        destinationSphere.frustumCulled = false;

        const cameraWorldPosition = iq3Camera.getConfigurationWorld().position.clone();
        wayfindingManager.updateDestinationSphereScale(destinationSphere, cameraWorldPosition);

        return destinationSphere;
    }

    getStartPosition() {
        let fromPos = iq3Camera.getConfigurationWorld().position;
        const cameraDirection = new THREE.Vector3();
        iq3Camera.camera.getWorldDirection(cameraDirection);

        const rightDirection = cameraDirection.clone().cross(new THREE.Vector3(0, 1, 0)).normalize();
        const bottomDirection = cameraDirection.clone().cross(rightDirection).normalize();

        const offsetLength = 0.4;

        fromPos.add(rightDirection.multiplyScalar(offsetLength * 0.45));
        fromPos.add(bottomDirection.multiplyScalar(offsetLength * 0.20));

        return fromPos.add(cameraDirection.multiplyScalar(offsetLength));
    }

    getDestinationPosition() {
        let position = this.selectedPoint.clone();
        if (this.volume.id) {
            let positionInModelSpace = getPosafterOffset(position, this.modelId, this.volume.id, true);
            position = this.getPositionForPoint(positionInModelSpace, this.modelId);
        } else {
            position = this.getPositionForPoint(position, this.modelId);
        }

        return position.clone();
    }

    getPositionForPoint(position, modelId) {
        const modelObject = getModelObject(modelId);
        if (modelObject) {
            position = modelObject.localToWorld(position);
        }
        position = VR.scene.worldToLocal(position);
        return position;
    }

    getCircleTexture() {
        const circleTextureConstants =  WayfindingConstants.circleCanvasTexture;
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = circleTextureConstants.width;

        const context = canvas.getContext('2d');
        context.fillStyle = circleTextureConstants.fillColor;
        context.beginPath();
        context.arc(circleTextureConstants.x, circleTextureConstants.y, circleTextureConstants.radius,
            circleTextureConstants.startAngle, circleTextureConstants.endAngle);
        context.fill();

        return new THREE.CanvasTexture(canvas);
    }

    prepareUserData() {
        this.wayfindingLine.userData = {
            id: this.id,
            model: this.model,
            volume: this.volume,
            distanceThreshold: this.distanceThreshold,
            pathColor: this.pathColor,
            selectedPoint: this.selectedPoint,
            timelines: this.timelines,
            events: this.events,
            triggerType: this.triggerType,
            visible: this.visible,
        };

        this.wayfindingLine.listHandlerArray = this.getHandlerForWayfindingLine();
    }

    getHandlerForWayfindingLine() {
        let self = this;
        let handlerArray = [];

        const modelId = this.modelId || this.model.id;
        const branchId = this.volume.id;

        if (modelManager.getVolume(branchId) !== undefined) {

            handlerArray.push({
                handlerType: HANDLER_TYPE_ENUM.WAYFINDING,
                modifiedByType: HANDLER_TYPE_ENUM.VOLUME_MOVE,
                params: [],
                callback: self.moveWayfindingLine.bind(this)
            });

            handlerArray.push({
                handlerType: HANDLER_TYPE_ENUM.WAYFINDING,
                modifiedByType: HANDLER_TYPE_ENUM.VOLUME_VISIBILITY,
                params: [],
                callback: self.updateVisibilityOfWayfindingLine.bind(this)
            });

            handlerArray.push({
                handlerType: HANDLER_TYPE_ENUM.WAYFINDING,
                modifiedByType: HANDLER_TYPE_ENUM.VOLUME_MOVE_FOR_SCENE_OBJECT,
                params: [],
                callback: self.moveWayfindingLine.bind(this)
            });
        }

        if (modelId !== "") {
            handlerArray.push({
                handlerType: HANDLER_TYPE_ENUM.WAYFINDING,
                modifiedByType: HANDLER_TYPE_ENUM.MODEL_MOVE,
                params: [],
                callback: self.moveWayfindingLine.bind(this)
            });

            handlerArray.push({
                handlerType: HANDLER_TYPE_ENUM.WAYFINDING,
                modifiedByType: HANDLER_TYPE_ENUM.FLOOR,
                params: [],
                callback: self.moveWayfindingLine.bind(this)
            });

            handlerArray.push({
                handlerType: HANDLER_TYPE_ENUM.WAYFINDING,
                modifiedByType: HANDLER_TYPE_ENUM.MODEL_VISIBILITY,
                params: [],
                callback: self.updateVisibilityOfWayfindingLine.bind(this)
            });

            handlerArray.push({
                handlerType: HANDLER_TYPE_ENUM.WAYFINDING,
                modifiedByType: HANDLER_TYPE_ENUM.MODEL_MOVE_FOR_SCENE_OBJECT,
                params: [],
                callback: self.moveWayfindingLine.bind(this)
            });

            handlerArray.push({
                handlerType: HANDLER_TYPE_ENUM.WAYFINDING,
                modifiedByType: HANDLER_TYPE_ENUM.SCALE,
                params: [],
                callback: self.moveWayfindingLine.bind(this)
            });
        }

        handlerArray.push({
            handlerType: HANDLER_TYPE_ENUM.WAYFINDING,
            modifiedByType: HANDLER_TYPE_ENUM.CLIP_PLANE,
            params: [],
            callback: self.updateVisibilityOfWayfindingLine.bind(this)
        });

        handlerArray.push({
            handlerType: HANDLER_TYPE_ENUM.WAYFINDING,
            modifiedByType: HANDLER_TYPE_ENUM.CLIP_BOX,
            params: [],
            callback: self.updateVisibilityOfWayfindingLine.bind(this)
        });

        handlerArray.push({
            handlerType: HANDLER_TYPE_ENUM.WAYFINDING,
            modifiedByType: HANDLER_TYPE_ENUM.FLOOR,
            params: [],
            callback: self.moveWayfindingLine.bind(this)
        });

        return handlerArray;
    }

    moveWayfindingLine() {
        const lineId = this.id;
        let wayfindingLine = wayfindingManager.getWayfindingLine(lineId);
        this.moveLine();

        let destinationPosition = this.getDestinationPosition();
        if (wayfindingLine && destinationPosition) {
            this.updateVisibilityOfWayfindingLine(true);
        }
    }

    moveLine() {
        const lineId = this.id;
        let wayfindingLine = wayfindingManager.getWayfindingLine(lineId);

        if (!wayfindingLine) return;

        const modelId = this.modelId || this.model.id;
        if (modelId === "") {
            return;
        }

        this.performWayfindingCalculations();
    }

    updateVisibilityOfWayfindingLine(visibility) {
        let self = this;
        if (!self.wayfindingLine) {
            return;
        }

        const getVisibility = (visible) => {
            if (!self.visible) {
                return false;
            }

            const modelObject = getModelObject(self.modelId);
            if (!modelObject || !modelManager.isVisible(self.modelId) || !modelObject.visible) {
                return false;
            }

            const destinationPosition = self.getDestinationPosition();
            const isInsideClipBox = clipPlaneController.isPointInsideClipBox(destinationPosition);
            const isInsidePointCloud = modelObject.meshType === MeshTypeEnum.POINT_CLOUD ? pointCloudController.isPointInside(destinationPosition) : true;

            let volumeVisibility = true;
            const branchId = self.volume.id;
            if (branchId && modelManager.getVolume(branchId)) {
                volumeVisibility = modelManager.getVolume(branchId).volumeVisibility;
            }

            visible = volumeVisibility ? visible : false;

            return visible
                && isInsideClipBox
                && isInsidePointCloud
                && !modelManager.isModelAnimating(self.modelId);
        };

        visibility = getVisibility(visibility);
        self.wayfindingLine.visible = visibility;
    }

    async stop() {
        this._stopper();
        wayfindingManager.removeWayfindingAction(this.actionId);
        this.wayfindingLine = null;
    }
}

export default Wayfinding;
